<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800"
  >
    <v-card>
      <v-card-title class="text-h5">
        Relative Party's {{ title }}
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="selectedRecordId"
          :items="partyRecords"
          label="Record"
          outlined
          item-value="id"
        >
          <template #item="{ item }">
            #{{ item.id }} {{ item.record_holder }}
          </template>
          <template #selection="{ item }">
            #{{ item.id }} {{ item.record_holder }}
          </template>
        </v-autocomplete>

        <v-autocomplete
          v-if="selectedRecord"
          v-model="selectedLegalCase"
          :items="legalCases"
          label="Legal Case"
          outlined
          item-text="label"
          item-value="id"
        />

        <v-autocomplete
          v-if="selectedLegalCase"
          v-model="selectedPartyId"
          :items="availableParties"
          :loading="loadingParties"
          label="Party"
          item-value="id"
          outlined
        >
          <template #item="{ item }">
            #{{ item.id }}  {{ partyName(item) }}
            <v-spacer />
            <span
              v-if="isPartyContact(item)"
              class="text-overline px-2"
            >(contact)</span>
          </template>
          <template #selection="{ item }">
            #{{ item.id }}  {{ partyName(item) }}
          </template>
        </v-autocomplete>

        <v-divider />

        <PEPartySectionPreview
          v-for="section of sections"
          :key="section.id"
          :section="section"
          :property="property"
          :no-data-text="'No other than current party'"
          @input="onSectionSelect"
        />

        <v-card
          v-if="noSections && selectedPartyId"
          class="my-2"
        >
          <v-card-text class="text-h5">
            <v-row>
              <v-spacer />
              <v-col cols="auto">
                <span class="text-overline">No Results</span>
              </v-col>
              <v-spacer />
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="green darken-1"
          text
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary darken-1"
          text
          :disabled="!hasSelectedSections || noSections"
          @click="onAdd"
        >
          Add
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import { isObject } from 'lodash'

import { partyName } from '@/utilities/party'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { PARTY_NAMESPACE } from '@/store/party'
import { RECORD_NAMESPACE } from '@/store/record'

import PEPartySectionPreview from './PEPartySectionPreview.vue'

export default {
  name: 'PEPartyRelativeSectionDialog',

  components: {
    PEPartySectionPreview,
  },

  props: {
    onSelect: {
      type: Function,
      required: true,
    },

    property: {
      type: String,
      required: true,
    },

    title: {
      type: String,
      required: true,
    },

    value: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {
      availableParties: [],
      loadingParties: false,
      selectedLegalCase: null,
      selectedPartyId: null,
      selectedRecordId: null,
      selectedSections: {},
    }
  },

  computed: {
    dialog: {
      get () { return this.value },

      set (val) {
        this.$emit('input', val)
      },
    },

    hasSelectedSections () {
      return Object.values(this.selectedSections).filter(data => !!data).length > 0
    },

    legalCases () {
      return this.selectedRecord ? this.selectedRecord.legal_cases : []
    },

    noSections () {
      return this.sections.length === 0
    },

    partyRecords () {
      return this.peParty?.records || []
    },

    sections () {
      return this.selectedParty ? this.selectedParty[this.property] : []
    },

    selectedParty () {
      return this.availableParties.find(party => party.id === this.selectedPartyId)
    },

    selectedRecord () {
      return this.partyRecords.find(record => record.id === this.selectedRecordId)
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'currentLegalCase',
    ]),

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
    ]),
  },

  watch: {
    selectedLegalCase (val) {
      if (val) this.loadParties()
      else this.availableParties = []
    },

    value (val) {
      if (!val) {
        this.availableParties = []
        this.loadingParties = false
        this.selectedLegalCase = null
        this.selectedPartyId = null
        this.selectedRecordId = null
        this.availableParties = []
        this.selectedSections = {}
      } else {
        this.selectedRecordId = this.currentRecord?.id
        this.selectedLegalCase = this.currentLegalCase?.id
      }
    },
  },

  methods: {
    isObject,

    isPartyContact (party) {
      return party.id === this.selectedRecord?.contact_party_id
    },

    async loadParties () {
      this.loadingParties = true
      this.availableParties = await this.fetchLegalCaseParties({ id: this.selectedLegalCase })
      this.availableParties = this.availableParties.filter(party => party.id !== this.peParty.id)
      this.loadingParties = false
    },

    onAdd () {
      const sections = Object.values(this.selectedSections).filter(data => !!data)
      this.onSelect(sections)
      this.dialog = false
    },

    onSectionSelect (data) {
      if (data.selected) Vue.set(this.selectedSections, data.id, data.section)
      else Vue.set(this.selectedSections, data.id, null)
    },

    partyName,

    ...mapActions(PARTY_NAMESPACE, [
      'fetchLegalCaseParties',
    ]),
  },
}
</script>
